// ABOUTME: The small decisions the list makes about time and steps: whether a move happened today in Berlin, how many
// ABOUTME: Berlin days since the last move, which applications go first, and which steps are on the way rather than exits.
import { formatInTimeZone } from 'date-fns-tz';
import { InterviewStatus } from 'modules/interviews/types';
import { DEFAULT_TIMEZONE } from 'utils/date';
import {
  readSession,
  readSessionJson,
  writeSession,
  writeSessionJson
} from 'utils/storage';
import {
  APPLICATIONS_BLOCK_OPEN_KEY,
  APPLICATIONS_BLOCK_SEEN_KEY
} from './constants';
import {
  TalentApplication,
  TalentApplicationStatus,
  TalentApplicationStep,
  TalentApplicationStepState
} from './types';

const DAY = 'yyyy-MM-dd';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The Berlin calendar day of an instant. The app's talents are in Germany; the device's zone is not the point. */
const berlinDay = (instant: string | Date): string =>
  formatInTimeZone(new Date(instant), DEFAULT_TIMEZONE, DAY);

/** Whole Berlin calendar days from the move to now: a move at 23:50 read at 00:10 is one day, not zero. */
export const daysSince = (lastChangedAt: string, now: Date): number =>
  Math.round(
    (Date.parse(berlinDay(now)) - Date.parse(berlinDay(lastChangedAt))) /
      MS_PER_DAY
  );

/** An active application whose latest move happened today in Berlin. An outcome is not a move worth a chip. */
export const movedToday = (
  application: TalentApplication,
  now: Date
): boolean =>
  application.status === TalentApplicationStatus.ACTIVE &&
  !!application.lastChangedAt &&
  berlinDay(application.lastChangedAt) === berlinDay(now);

/**
 * The applications that moved today first, the most recent move first among them; everything else keeps the order
 * it came in. A stable partition, so the caller's own order (newest first) survives underneath.
 */
export const movedTodayFirst = (
  applications: TalentApplication[],
  now: Date
): TalentApplication[] => {
  const moved = applications
    .filter(application => movedToday(application, now))
    .sort((a, b) =>
      (b.lastChangedAt ?? '').localeCompare(a.lastChangedAt ?? '')
    );
  const rest = applications.filter(
    application => !movedToday(application, now)
  );
  return [...moved, ...rest];
};

const isExit = (step: TalentApplicationStep) =>
  step.outcome === 'REJECTED' || step.outcome === 'WITHDRAWN';

/**
 * The steps the full tracker draws (#484). The path is the stages and the hired outcome in the order given; a step on it
 * keeps its tick only while it lies before the current one, so a move back (proposed to first_call) no longer leaves a
 * tick on a step the talent is not past: the backend's DONE means "was there once", which drawn after the current step
 * read as "already passed". Rejected and withdrawn are exits, not steps anyone walks towards, so they are drawn only
 * once reached, after the path; until then they are not shown at all. With an exit current, the path keeps the ticks
 * the backend gave, since every one of those steps lies before the end.
 * A step the talent was never in that lies behind them (added straight into the interview, so cv_pending and applied
 * were skipped) is left out: an empty circle there read as something still to do, and nothing about it is.
 */
export const trackerSteps = (
  steps: TalentApplicationStep[]
): TalentApplicationStep[] => {
  const path = steps.filter(step => !isExit(step));
  const reachedExit = steps.filter(
    step => isExit(step) && step.state === TalentApplicationStepState.CURRENT
  );
  const currentIndex = path.findIndex(
    step => step.state === TalentApplicationStepState.CURRENT
  );
  // Behind the talent: before the current step, or with an exit current (no current on the path), up to the last tick.
  const behindUntil =
    currentIndex >= 0
      ? currentIndex
      : path
          .map(step => step.state)
          .lastIndexOf(TalentApplicationStepState.DONE);
  const drawnPath = path
    .map((step, index) =>
      index > currentIndex &&
      currentIndex >= 0 &&
      step.state === TalentApplicationStepState.DONE
        ? { ...step, state: TalentApplicationStepState.UPCOMING }
        : step
    )
    .filter(
      (step, index) =>
        !(
          index < behindUntil &&
          step.state === TalentApplicationStepState.UPCOMING
        )
    );
  return [...drawnPath, ...reachedExit];
};

/**
 * The steps the compact bar on the list card draws: the tracker's path (trackerSteps) without the exits. Rejected and
 * withdrawn are exits, not steps on the way; nobody walks towards them, so they get no segment. An older payload
 * without `outcome` is read as all stages. Reading the states off trackerSteps keeps the card's faded segments and its
 * "k done" equal to the detail's ticks: after a move back, a step past the current one is not done there either.
 */
export const barSteps = (
  steps: TalentApplicationStep[]
): TalentApplicationStep[] =>
  trackerSteps(steps).filter(step => !step.outcome || step.outcome === 'HIRED');

/** "Schritt 4 von 5": the current step's place on the way (the exits are no steps on it); nothing off the way. */
export const stepPosition = (
  steps: TalentApplicationStep[]
): { current: number; total: number } | undefined => {
  const way = barSteps(steps);
  const index = way.findIndex(
    step => step.state === TalentApplicationStepState.CURRENT
  );
  return index < 0 ? undefined : { current: index + 1, total: way.length };
};

/** How many steps on the way the talent is past: the card's "k done", by the same rule as the detail's ticks. */
export const doneStepCount = (steps: TalentApplicationStep[]): number =>
  barSteps(steps).filter(step => step.state === TalentApplicationStepState.DONE)
    .length;

/**
 * How the detail shows the application's latest interview (#484). The card is the current interview, so it is shown
 * only while the application is open and the round is not over by cancellation or expiry: a first-call interview is
 * cancelled when the candidate moves on, and drawn under a signed contract it read as a step backwards. Such a round is
 * a one-line note while the application is open (cancelled and expired each in their own words), and nothing once it
 * has an outcome.
 */
export const interviewDisplay = (
  application: TalentApplication
): 'card' | 'history' | 'none' => {
  const { interview } = application;
  if (!interview || application.status !== TalentApplicationStatus.ACTIVE) {
    return 'none';
  }
  const ended =
    interview.status === InterviewStatus.CANCELLED ||
    interview.status === InterviewStatus.EXPIRED;
  return ended ? 'history' : 'card';
};

/** The block opens by itself when a move has not been seen yet; otherwise it is the quiet row, unless the talent chose. */
/** The talent's own toggle this session wins; then what the feed says (PJL), then whether something moved. */
export const initialBlockOpen = (
  unseenMovedCount: number,
  feedOpen?: boolean
): boolean => {
  const stored = readSession(APPLICATIONS_BLOCK_OPEN_KEY);
  if (stored === '1') return true;
  if (stored === '0') return false;
  return feedOpen ?? unseenMovedCount > 0;
};

export const rememberBlockOpen = (open: boolean): void =>
  writeSession(APPLICATIONS_BLOCK_OPEN_KEY, open ? '1' : '0');

/** Which moves the talent has seen open in the block this session: the application's refId and the move it had then. */
export type SeenMoves = Record<string, string>;

export const seenMoves = (): SeenMoves =>
  readSessionJson<SeenMoves>(APPLICATIONS_BLOCK_SEEN_KEY, {});

/** The moved-today applications as seen now, by identity and move, so a later move of the same application counts again. */
export const rememberSeenMoves = (
  applications: TalentApplication[],
  now: Date
): void =>
  writeSessionJson(
    APPLICATIONS_BLOCK_SEEN_KEY,
    Object.fromEntries(
      applications
        .filter(application => movedToday(application, now))
        .map(application => [
          application.refId,
          application.lastChangedAt ?? ''
        ])
    )
  );

/** The applications that moved today and whose latest move the talent has not seen open in the block. */
export const unseenMoved = (
  applications: TalentApplication[],
  now: Date,
  seen: SeenMoves
): TalentApplication[] =>
  applications.filter(
    application =>
      movedToday(application, now) &&
      seen[application.refId] !== (application.lastChangedAt ?? '')
  );
